import { DateTime } from "luxon";
import { useState } from "react";

import { useToast } from "@/app/providers/ToastProvider";
import { FormErrorSummary } from "@/components/common/FormErrorSummary";
import { FormField } from "@/components/common/FormField";
import { LoadingOverlay } from "@/components/common/LoadingOverlay";
import { NumericStepper } from "@/components/common/NumericStepper";
import { SelectField } from "@/components/common/SelectField";
import { unitLabelFor } from "@/components/common/UnitPicker";
import { BackButton } from "@/components/ui/back-button";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NAFA_FORM_WIDTH } from "@/components/ui/page-container";
import { PageHeader } from "@/components/ui/page-header";
import { useProductStocks } from "@/features/product-stocks/hooks/useProductStocks";
import { useCreateSupplying } from "@/features/product-stocks/hooks/useSupplying";
import { useProducts } from "@/features/products/hooks/useProducts";
import { useProvisionStocks } from "@/features/provision-stocks/hooks/useProvisionStocks";
import { useProvisions } from "@/features/provisions/hooks/useProvisions";
import { useRecipes } from "@/features/recipes/hooks/useRecipes";
import { getApiValidationErrors } from "@/lib/apiClient";
import { expiryInstant, toLocalDateTime } from "@/lib/datetime";
import { formatQuantity } from "@/lib/format";
import { focusFirstField } from "@/lib/forms";
import { toErrorMessage } from "@/lib/swr";

type FieldErrors = {
  provisionStockId?: string;
  productStockId?: string;
  recipeId?: string;
  quantity?: string;
  expiryDate?: string;
};

const FIELD_IDS = {
  provisionStockId: "supplying-provision-stock",
  productStockId: "supplying-product-stock",
  recipeId: "supplying-recipe",
  quantity: "supplying-quantity",
  expiryDate: "supplying-expiry",
} as const;

const VALIDATION_MESSAGES: Record<string, string> = {
  "Supply date cannot be in the future.": "La date de ravitaillement ne peut pas être dans le futur.",
  "Expiry date must be in the future.": "La date de péremption doit être dans le futur.",
  "Provision stock ID is required.": "Choisissez le stock provisionnel source.",
  "Product stock ID is required.": "Choisissez le stock marchand d’arrivée.",
  "Recipe ID is required.": "Choisissez le produit à fabriquer.",
  "Quantity must be greater than 0.": "Indiquez une quantité supérieure à 0.",
};

function userValidationMessage(message: string): string {
  return VALIDATION_MESSAGES[message] ?? message;
}

export default function SupplyingPage({
  initialProductStockId,
  onBack,
  onDone,
}: {
  initialProductStockId: string;
  onBack: () => void;
  onDone: () => Promise<unknown> | void;
}) {
  const [provisionStockId, setProvisionStockId] = useState("");
  const [productStockId, setProductStockId] = useState(initialProductStockId);
  const [recipeId, setRecipeId] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [expiryDate, setExpiryDate] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string>();
  const toast = useToast();

  const { stocks: provisionStocks } = useProvisionStocks();
  const { stocks: productStocks } = useProductStocks();
  const { products } = useProducts();
  const { provisions } = useProvisions();
  const { recipes } = useRecipes();
  const { create, isCreating } = useCreateSupplying();

  const selectedProvisionStockId = provisionStockId || provisionStocks[0]?.id || "";
  const selectedProductStockId = productStockId || productStocks[0]?.id || "";
  const selectedRecipe = recipes.find((recipe) => recipe.id === recipeId);
  const selectedProductStock = productStocks.find((stock) => stock.id === selectedProductStockId);
  const selectedProduct = products.find((product) => product.id === selectedRecipe?.productId);
  const quantityStep = selectedProduct?.measurementPrecision ?? 1;
  const provisionById = new Map(provisions.map((provision) => [provision.id, provision]));
  const today = DateTime.now().toISODate() ?? "";

  const clearFieldError = (field: keyof FieldErrors) => {
    setFieldErrors((current) => (current[field] ? { ...current, [field]: undefined } : current));
    setFormError(undefined);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const errors: FieldErrors = {};
    if (!selectedProvisionStockId) errors.provisionStockId = "Choisissez le stock provisionnel source.";
    if (!selectedProductStockId) errors.productStockId = "Choisissez le stock marchand d’arrivée.";
    if (!recipeId) errors.recipeId = "Choisissez le produit à fabriquer.";
    if (!(Number(quantity) > 0)) errors.quantity = "Indiquez une quantité supérieure à 0.";

    if (expiryDate) {
      const parsedExpiryDate = DateTime.fromISO(expiryDate);
      if (!parsedExpiryDate.isValid || parsedExpiryDate.startOf("day") < DateTime.now().startOf("day")) {
        errors.expiryDate = "La date de péremption ne peut pas être antérieure à aujourd’hui.";
      }
    }

    setFieldErrors(errors);
    setFormError(Object.keys(errors).length > 0 ? "Corrigez les champs signalés avant de ravitailler." : undefined);
    const invalidIds = (Object.keys(FIELD_IDS) as Array<keyof FieldErrors>)
      .filter((field) => errors[field])
      .map((field) => FIELD_IDS[field]);
    focusFirstField(invalidIds);

    if (Object.keys(errors).length > 0) {
      toast.error(Object.values(errors)[0] ?? "Corrigez les champs signalés.");
      return;
    }

    try {
      await create({
        label: selectedRecipe?.name ?? "Ravitaillement",
        recipeId,
        provisionStockId: selectedProvisionStockId,
        productStockId: selectedProductStockId,
        quantity: Number(quantity),
        suppliedAt: toLocalDateTime(DateTime.now()),
        expiryDate: expiryDate ? (expiryInstant(expiryDate) ?? null) : null,
      });
    } catch (error) {
      const apiErrors = getApiValidationErrors(error);
      const nextErrors: FieldErrors = {
        provisionStockId: apiErrors.provisionStockId,
        productStockId: apiErrors.productStockId,
        recipeId: apiErrors.recipeId,
        quantity: apiErrors.quantity,
        expiryDate: apiErrors.expiryDate,
      };
      const translatedErrors = Object.fromEntries(
        Object.entries(nextErrors).map(([field, message]) => [
          field,
          message ? userValidationMessage(message) : message,
        ]),
      ) as FieldErrors;
      const fieldErrorIds = (Object.keys(FIELD_IDS) as Array<keyof FieldErrors>)
        .filter((field) => translatedErrors[field])
        .map((field) => FIELD_IDS[field]);
      setFieldErrors(translatedErrors);
      const summary =
        fieldErrorIds.length > 0 ? "Corrigez les champs signalés avant de ravitailler." : toErrorMessage(error);
      setFormError(summary ?? "Le ravitaillement n’a pas pu être enregistré.");
      focusFirstField(fieldErrorIds);
      toast.error(summary ?? "Le ravitaillement n’a pas pu être enregistré.");
      return;
    }

    toast.success(
      quantity +
        " × " +
        (selectedRecipe?.name ?? "production") +
        " ajouté au stock marchand" +
        (selectedProductStock ? " « " + selectedProductStock.name + " »" : "") +
        ".",
    );
    await onDone();
  };

  return (
    <form onSubmit={handleSubmit} className={`flex ${NAFA_FORM_WIDTH} flex-col gap-6 pb-28`}>
      {isCreating && <LoadingOverlay message="Ravitaillement en cours…" />}

      <div className="flex items-start gap-3">
        <BackButton onClick={onBack} />
        <PageHeader title="Ravitailler" subtitle="Transformez vos matières premières en produits vendables." />
      </div>

      <FormErrorSummary
        fields={[
          { id: FIELD_IDS.provisionStockId, label: "Stock provisionnel source", message: fieldErrors.provisionStockId },
          { id: FIELD_IDS.productStockId, label: "Stock marchand d’arrivée", message: fieldErrors.productStockId },
          { id: FIELD_IDS.recipeId, label: "Produit à fabriquer", message: fieldErrors.recipeId },
          { id: FIELD_IDS.quantity, label: "Quantité", message: fieldErrors.quantity },
          { id: FIELD_IDS.expiryDate, label: "Date de péremption", message: fieldErrors.expiryDate },
        ]}
      >
        {formError}
      </FormErrorSummary>

      <div className="rounded-xl border border-brand/40 bg-brand-soft p-4">
        <p className="font-semibold">Stocks du ravitaillement</p>
        <p className="mt-0.5 text-sm text-muted-foreground">Le stock unique est sélectionné automatiquement.</p>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <FormField
            label="Stock provisionnel source"
            htmlFor={FIELD_IDS.provisionStockId}
            error={fieldErrors.provisionStockId}
            errorId={FIELD_IDS.provisionStockId + "-error"}
          >
            <SelectField
              id={FIELD_IDS.provisionStockId}
              value={selectedProvisionStockId}
              onChange={(value) => {
                setProvisionStockId(value);
                clearFieldError("provisionStockId");
              }}
              disabled={provisionStocks.length <= 1}
              placeholder="Choisir un stock…"
              aria-invalid={Boolean(fieldErrors.provisionStockId)}
              aria-describedby={fieldErrors.provisionStockId ? FIELD_IDS.provisionStockId + "-error" : undefined}
              options={provisionStocks.map((stock) => ({ value: stock.id, label: stock.name }))}
            />
          </FormField>

          <FormField
            label="Stock marchand d’arrivée"
            htmlFor={FIELD_IDS.productStockId}
            error={fieldErrors.productStockId}
            errorId={FIELD_IDS.productStockId + "-error"}
          >
            <SelectField
              id={FIELD_IDS.productStockId}
              value={selectedProductStockId}
              onChange={(value) => {
                setProductStockId(value);
                clearFieldError("productStockId");
              }}
              disabled={productStocks.length <= 1}
              placeholder="Choisir un stock…"
              aria-invalid={Boolean(fieldErrors.productStockId)}
              aria-describedby={fieldErrors.productStockId ? FIELD_IDS.productStockId + "-error" : undefined}
              options={productStocks.map((stock) => ({ value: stock.id, label: stock.name }))}
            />
          </FormField>
        </div>
      </div>

      <FormField
        label="Produit à fabriquer"
        htmlFor={FIELD_IDS.recipeId}
        error={fieldErrors.recipeId}
        errorId={FIELD_IDS.recipeId + "-error"}
      >
        <SelectField
          id={FIELD_IDS.recipeId}
          value={recipeId}
          onChange={(value) => {
            setRecipeId(value);
            clearFieldError("recipeId");
          }}
          placeholder="Choisir une recette…"
          aria-invalid={Boolean(fieldErrors.recipeId)}
          aria-describedby={fieldErrors.recipeId ? FIELD_IDS.recipeId + "-error" : undefined}
          options={recipes.map((recipe) => ({ value: recipe.id, label: recipe.name }))}
        />
      </FormField>

      {selectedRecipe?.ingredients?.length ? (
        <div className="overflow-hidden rounded-lg border border-border">
          <p className="bg-muted/60 px-3 py-2 text-[13px] font-semibold text-muted-foreground">
            Matières consommées pour {quantity || "0"} unité(s)
          </p>
          <ul>
            {selectedRecipe.ingredients.map((ingredient) => (
              <li
                key={ingredient.provisionId}
                className="flex items-center justify-between gap-3 border-t border-muted px-3 py-2.5 text-sm"
              >
                <span className="min-w-0 truncate">
                  {provisionById.get(ingredient.provisionId)?.name ?? "Matière inconnue"}
                </span>
                <span className="shrink-0 font-semibold tabular-nums text-destructive">
                  −{" "}
                  {formatQuantity(
                    ingredient.quantity * (Number(quantity) || 0),
                    unitLabelFor(
                      provisionById.get(ingredient.provisionId)?.measurementUnit ?? "",
                      ingredient.quantity * (Number(quantity) || 0),
                    ),
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <FormField
        label="Quantité à ravitailler"
        htmlFor={FIELD_IDS.quantity}
        error={fieldErrors.quantity}
        errorId={FIELD_IDS.quantity + "-error"}
      >
        <NumericStepper
          id={FIELD_IDS.quantity}
          min={0}
          step={quantityStep}
          value={quantity}
          onChange={(value) => {
            setQuantity(String(value));
            clearFieldError("quantity");
          }}
          aria-label="Quantité à ravitailler"
          aria-invalid={Boolean(fieldErrors.quantity)}
          aria-describedby={fieldErrors.quantity ? FIELD_IDS.quantity + "-error" : undefined}
          inputClassName="text-lg font-semibold"
        />
      </FormField>

      <FormField
        label="À consommer avant"
        hint="Facultatif — la date doit rester dans le futur."
        htmlFor={FIELD_IDS.expiryDate}
        error={fieldErrors.expiryDate}
        errorId={FIELD_IDS.expiryDate + "-error"}
        hintId={FIELD_IDS.expiryDate + "-hint"}
      >
        <Input
          id={FIELD_IDS.expiryDate}
          type="date"
          value={expiryDate}
          min={today || undefined}
          onChange={(event) => {
            setExpiryDate(event.target.value);
            clearFieldError("expiryDate");
          }}
          aria-invalid={Boolean(fieldErrors.expiryDate)}
          aria-describedby={[
            FIELD_IDS.expiryDate + "-hint",
            fieldErrors.expiryDate ? FIELD_IDS.expiryDate + "-error" : null,
          ]
            .filter(Boolean)
            .join(" ")}
        />
      </FormField>

      <div className="flex gap-3">
        <Button type="submit" size="lg" disabled={isCreating}>
          Ravitailler
        </Button>
        <Button type="button" variant="outline" size="lg" onClick={onBack} disabled={isCreating}>
          Annuler
        </Button>
      </div>
    </form>
  );
}
